import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import styled from "@emotion/styled"
import { graphql, Link } from "gatsby"
import { RichText, Date } from "prismic-reactjs"
import dimensions from "styles/dimensions"
import ButtonBack from "../components/_ui/ButtonBack"
import WorkSlider from "components/WorkSlider"
import { Global } from "@emotion/core"
import globalStyles from "styles/global"
import "../styles/animations.scss"
import typeStyles from "styles/typography"

// delete photoswipe plugins when finished

const WorkHeroContainer = styled("div")`
  display: flex;
  margin-bottom: 120px;

  text-decoration: none;
  color: currentColor;

  @media (max-width: ${dimensions.maxwidthTablet}px) {
    flex-direction: column-reverse;
    margin-bottom: 60px;
  }
`
const WorkHeroContent = styled("div")`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;

  width: 30%;
  margin: 80px;

  h5 {
    font-weight: 500;
    margin-bottom: 32px;
  }

  h6,
  p:first-of-type {
    text-transform: uppercase;
  }

  p,
  span {
    font-size: 14px;
  }

  span.copyright {
    margin: 40px 0;
  }

  @media (max-width: ${dimensions.maxwidthTablet}px) {
    width: 100%;
    padding: 32px 32px 0 32px;
    margin: 0;

    h5 {
      margin-top: 0;
    }
  }
`

const WorkHeroImageContainer = styled("div")`
  width: 70%;
  margin: 80px 0;

  @media (max-width: 768px) {
    height: 100%;
    margin: 0;
    width: 100%;
  }
`

const EnquireButton = styled("button")`
  height: 52px;
  border: 1px black solid;
  padding: 12px 32px;
  margin-bottom: 40px;
  box-sizing: border-box;

  letter-spacing: 0.03em;
  background: white;
  font-family: "freight-sans-pro", sans-serif;
  font-size: 20px;
  font-weight: 500;
  text-transform: uppercase;

  & :hover {
    border: 2px black solid;
    cursor: pointer;
  }
`

const Work = ({ work, meta }) => {
  // transform date format
  const newDate = Date(work.work_date)
  const formattedDate = Intl.DateTimeFormat("de-DE", {
    year: "numeric",
  }).format(newDate)

  return (
    <>
      <Helmet
        title={`${work.work_title[0].text} | Artwork | Script Art`}
        titleTemplate={`%s | ${meta.title}`}
        meta={[
          {
            name: `description`,
            content: meta.description,
          },
          {
            property: `og:title`,
            content: `${work.work_title[0].text} | Artworks | Script Art`,
          },
          {
            property: `og:description`,
            content: meta.description,
          },
          {
            property: `og:type`,
            content: `website`,
          },
          {
            name: `twitter:card`,
            content: `summary`,
          },
          {
            name: `twitter:creator`,
            content: meta.author,
          },
          {
            name: `twitter:title`,
            content: meta.title,
          },
          {
            name: `twitter:description`,
            content: meta.description,
          },
        ].concat(meta)}
      />

      <Global styles={[globalStyles, typeStyles]} />
      <ButtonBack />
      <WorkHeroContainer>
        <WorkHeroContent>
          <h5>{work.work_artist_name[0].text}</h5>

          <h6>
            {work.work_title[0].text}, {formattedDate}
          </h6>

          {RichText.render(work.work_material)}

          <span>
            {work.work_width} x {work.work_height} CM
          </span>

          <span className="copyright">Copyright the artist.</span>

          <Link to={`/contact`}>
            <EnquireButton>Enquire</EnquireButton>
          </Link>

          {RichText.render(work.work_description)}
        </WorkHeroContent>

        <WorkHeroImageContainer>
          <WorkSlider galleryImages={work.work_gallery} title="hello" />
        </WorkHeroImageContainer>
      </WorkHeroContainer>
    </>
  )
}

export default ({ data }) => {
  const workContent = data.prismic.allWorks.edges[0].node
  const meta = data.site.siteMetadata
  return <Work work={workContent} meta={meta} />
}

Work.propTypes = {
  work: PropTypes.object.isRequired,
}

export const query = graphql`
  query WorkQuery($uid: String) {
    prismic {
      allWorks(uid: $uid) {
        edges {
          node {
            _meta {
              uid
            }
            work_artist_name
            work_date
            work_description
            work_gallery {
              gallery_image
            }
            work_height
            work_material
            work_title
            work_width
          }
        }
      }
    }
    site {
      siteMetadata {
        title
        description
        author
      }
    }
  }
`
